/**
 * Feature Flags Configuration
 * Simple, flexible system for enabling/disabling features
 *
 * Usage:
 *   import { isFeatureEnabled } from '../config/featureFlags.js';
 *   if (isFeatureEnabled('someFeature')) { ... }
 */

/**
 * Feature flag definitions
 * Set to true to enable, false to disable
 */
const FEATURE_FLAGS = {
    // Publisher narration ("Luister na Kassie") on index page
    publisherNarration: false,
    // Recording diagnostic overlay (🩺 icon, top-left)
    recordingDebug: true,
    // Show 🩺 icon always (not just during story-reader) for auth diagnostics
    diagnosticAlwaysVisible: false,
};

/**
 * Release profiles for different deployment stages
 * Uncomment the profile you want to use, or set via environment variable
 */
const RELEASE_PROFILES = {
    // Voorsmakie (Preview/Beta) - Stable features only
    voorsmakie: {
        publisherNarration: false,
        recordingDebug: true,
        diagnosticAlwaysVisible: false,
    },

    // Full release - All stable features enabled
    full: {
        publisherNarration: true,
        recordingDebug: false,
        diagnosticAlwaysVisible: false,
    },

    // Development - All features enabled for testing
    development: {
        publisherNarration: true,
        recordingDebug: true,
        diagnosticAlwaysVisible: true,
    },
};

/**
 * Get the active release profile
 * Priority: URL param > localStorage > environment variable > default
 */
function getActiveProfile() {
    // Check URL parameter (for easy testing: ?profile=development)
    const urlParams = new URLSearchParams(window.location.search);
    const urlProfile = urlParams.get('profile');
    if (urlProfile && RELEASE_PROFILES[urlProfile]) {
        return urlProfile;
    }

    // Check localStorage (persists across sessions)
    const storedProfile = localStorage.getItem('featureProfile');
    if (storedProfile && RELEASE_PROFILES[storedProfile]) {
        return storedProfile;
    }

    // Check environment variable (set in .env or build config)
    // In Vite: import.meta.env.VITE_FEATURE_PROFILE
    if (typeof import.meta !== 'undefined' && import.meta.env?.VITE_FEATURE_PROFILE) {
        const envProfile = import.meta.env.VITE_FEATURE_PROFILE;
        if (RELEASE_PROFILES[envProfile]) {
            return envProfile;
        }
    }

    // Default profile
    return 'voorsmakie';
}

/**
 * Get the current feature flags based on active profile
 */
function getFeatureFlags() {
    const profile = getActiveProfile();
    return RELEASE_PROFILES[profile] || FEATURE_FLAGS;
}

/**
 * Check if a feature is enabled
 * @param {string} featureName - Name of the feature to check
 * @returns {boolean} - True if feature is enabled
 */
export function isFeatureEnabled(featureName) {
    const flags = getFeatureFlags();
    return flags[featureName] ?? false;
}

/**
 * Get all enabled features
 * @returns {string[]} - Array of enabled feature names
 */
export function getEnabledFeatures() {
    const flags = getFeatureFlags();
    return Object.keys(flags).filter(key => flags[key]);
}

/**
 * Get the current profile name
 * @returns {string} - Current profile name
 */
export function getCurrentProfile() {
    return getActiveProfile();
}

/**
 * Set the feature profile (useful for testing)
 * @param {string} profileName - Name of the profile to activate
 */
export function setFeatureProfile(profileName) {
    if (RELEASE_PROFILES[profileName]) {
        localStorage.setItem('featureProfile', profileName);
        console.log(`✅ Feature profile set to: ${profileName}`);
        console.log('🔄 Reload the page to apply changes');
        return true;
    }
    console.error(`❌ Unknown profile: ${profileName}`);
    console.log('Available profiles:', Object.keys(RELEASE_PROFILES));
    return false;
}

/**
 * Clear the stored profile (revert to default)
 */
export function clearFeatureProfile() {
    localStorage.removeItem('featureProfile');
    console.log('✅ Feature profile cleared, using default');
    console.log('🔄 Reload the page to apply changes');
}

// Expose to window for easy console access during development
if (typeof window !== 'undefined') {
    window.featureFlags = {
        isEnabled: isFeatureEnabled,
        getEnabled: getEnabledFeatures,
        getCurrentProfile,
        setProfile: setFeatureProfile,
        clearProfile: clearFeatureProfile,
        availableProfiles: Object.keys(RELEASE_PROFILES),
    };

    // Log current profile on load
    console.log(`🚀 Child App Feature Profile: ${getCurrentProfile()}`);
    console.log(`✨ Enabled Features:`, getEnabledFeatures());
}

export default {
    isFeatureEnabled,
    getEnabledFeatures,
    getCurrentProfile,
    setFeatureProfile,
    clearFeatureProfile,
};
